import { useState, useMemo, useEffect, useCallback } from 'react'
import {
  Alert,
  Card,
  Collapse,
  Grid,
  Group,
  Loader,
  MultiSelect,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Badge,
  Pagination,
  Tooltip,
  UnstyledButton,
  ActionIcon,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconCheck,
  IconSearch,
  IconGavel,
  IconRobot,
  IconUserCheck,
  IconHelpCircle,
  IconClock,
  IconChevronDown,
  IconChevronRight,
  IconArrowUp,
  IconArrowDown,
  IconArrowsSort,
  IconDownload,
} from '@tabler/icons-react'
import { BarChart } from '@mantine/charts'
import { PageHeader, FilterBar, MetricCard, ChartCard, getPresetRange } from '@/components/shared'
import { useMeteringDashboard, useTelemetryDashboard, useRoutingRows } from '@/hooks/useAnalytics'
import type { ClientMeteringRow } from '@/types'
import { usePermissions } from '@/hooks/usePermissions'
import { OffenseDetailDialog } from '@/components/OffenseDetailDialog'

const fmt = (n: number) => n.toLocaleString()
const pct = (n: number, total: number) => (total > 0 ? ((n / total) * 100).toFixed(1) : '0.0')
const fmtQueue = (n: number, hasDecisions: boolean) => (hasDecisions ? fmt(n) : '-')

const fmtMs = (ms: number | null) => ms != null ? `${Math.round(ms).toLocaleString()}ms` : '-'

type InsuffReason = 'MISSING_INFO' | 'LOW_CONFIDENCE' | 'INSUFFICIENT_DATA' | 'MODEL_ENHANCEMENTS' | 'IDENTITY'

const INSUFF_REASON_LABEL: Record<InsuffReason, string> = {
  MISSING_INFO: 'MR - missing info',
  LOW_CONFIDENCE: 'MR - low confidence',
  INSUFFICIENT_DATA: 'Insufficient Data',
  MODEL_ENHANCEMENTS: 'Model Enhancements',
  IDENTITY: 'Identity Matching Issues',
}

function QueueDistributionBars({
  automation,
  auditor,
  insufficientData,
  nested,
  total,
  hasDecisions,
  automationLabel = 'Automation',
  auditorLabel = 'Auditor',
  insuffLabel = 'Insufficient Data',
}: {
  automation: number
  auditor: number
  insufficientData: number
  nested: { key: InsuffReason; count: number; color: string }[]
  total: number
  hasDecisions: boolean
  automationLabel?: string
  auditorLabel?: string
  insuffLabel?: string
}) {
  const insuff = insufficientData
  return (
    <Stack gap="md" py="md">
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="sm">{automationLabel}</Text>
          <Text size="sm" fw={600}>{fmtQueue(automation, hasDecisions)}</Text>
        </Group>
        <Progress value={hasDecisions ? Number(pct(automation, total)) : 0} color="teal" size="lg" radius="md" />
      </div>
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="sm">{auditorLabel}</Text>
          <Text size="sm" fw={600}>{fmtQueue(auditor, hasDecisions)}</Text>
        </Group>
        <Progress value={hasDecisions ? Number(pct(auditor, total)) : 0} color="red" size="lg" radius="md" />
      </div>
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="sm">{insuffLabel}</Text>
          <Text size="sm" fw={600}>{fmtQueue(insuff, hasDecisions)}</Text>
        </Group>
        <Progress value={hasDecisions ? Number(pct(insuff, total)) : 0} color="yellow" size="lg" radius="md" />
        <Stack gap="xs" mt="sm" pl="md">
          {nested.map((item) => (
            <div key={item.key}>
              <Group justify="space-between" mb={4}>
                <Text size="xs" c="dimmed">{INSUFF_REASON_LABEL[item.key]}</Text>
                <Text size="xs" fw={600}>
                  {fmtQueue(item.count, hasDecisions)}
                  {hasDecisions && insuff > 0 ? ` / ${pct(item.count, insuff)}% of MR` : ''}
                </Text>
              </Group>
              <Progress
                value={hasDecisions ? Number(pct(item.count, insuff)) : 0}
                color={item.color}
                size="sm"
                radius="md"
              />
            </div>
          ))}
        </Stack>
      </div>
    </Stack>
  )
}

const toLocalYmd = (d: Date | null) => {
  if (!d) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const addDaysYmd = (ymd: string, days: number) => {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  dt.setDate(dt.getDate() + days)
  return toLocalYmd(dt)
}

const formatHourLabel = (iso: string, singleDay: boolean) => {
  const hour = Number(iso.slice(11, 13))
  const label = hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`
  if (singleDay) return label
  return `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))} ${label}`
}

const formatDayLabel = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const METERING_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '1y', value: '1y' },
  { label: 'Custom', value: 'custom' },
]

const DETAIL_COLUMNS = [
  { value: 'search_id', label: 'Search ID' },
  { value: 'offense_id', label: 'Offense ID' },
  { value: 'case_number', label: 'Case Number' },
  { value: 'search_type', label: 'Product' },
  { value: 'source_norm', label: 'Source Norm' },
  { value: 'offense_type', label: 'Offense Type' },
  { value: 'type_class', label: 'Type Class' },
  { value: 'subtype_class', label: 'Subtype Class' },
  { value: 'charge', label: 'Charge' },
  { value: 'charge_norm', label: 'Charge Norm' },
  { value: 'disposition', label: 'Disposition' },
  { value: 'disposition_norm', label: 'Disp Norm' },
  { value: 'disp_class', label: 'Disp Class' },
  { value: 'disposition_date', label: 'Disp Date' },
  { value: 'offense_age', label: 'Offense Age' },
  { value: 'adjudication_category', label: 'Adjudication' },
  { value: 'identity_level', label: 'AI Identity Match Level' },
  { value: 'reportability', label: 'Routing Reportability' },
  { value: 'queue', label: 'Queue' },
  { value: 'ce_decision', label: 'AI Decision' },
  { value: 'final_decision', label: 'Final Decision' },
  { value: 'rules_applied', label: 'Rules Applied' },
  { value: 'rationale', label: 'AI Rationale' },
]

const DEFAULT_VISIBLE_COLUMNS = [
  'search_id', 'offense_id', 'case_number', 'search_type', 'charge',
  'charge_norm', 'disposition', 'disposition_norm', 'identity_level', 'reportability', 'queue', 'ce_decision', 'final_decision',
]

const cellText = (val: unknown): string => {
  if (val === null || val === undefined) return 'None'
  if (Array.isArray(val)) return val.join(', ')
  if (typeof val === 'object') {
    const boxed = val as { value?: unknown }
    if (boxed.value != null) return String(boxed.value)
    return JSON.stringify(val)
  }
  return String(val)
}

function renderDetailCell(record: Record<string, unknown>, colValue: string) {
  const value = record[colValue]
  switch (colValue) {
    case 'search_id':
      return <Text size="xs" ff="monospace" c="blue" td="underline" style={{ cursor: 'pointer' }}>{cellText(value)}</Text>
    case 'offense_id':
      return <Text size="xs" ff="monospace">{cellText(value)}</Text>
    case 'identity_level':
      return (
        <Badge
          size="sm"
          color={
            value === 'High' ? 'green'
              : value === 'Medium' ? 'orange'
                : value === 'Not Matching' ? 'red'
                  : value === 'Not Enough Info' ? 'yellow' : 'gray'
          }
        >
          {cellText(value)}
        </Badge>
      )
    case 'queue': {
      const q = String(value ?? '').toUpperCase().replace(/\s+/g, '_')
      return (
        <Badge
          size="sm"
          color={q === 'AUTOMATION' ? 'green' : q === 'AUDITOR' ? 'orange' : q === 'INSUFFICIENT_DATA' ? 'yellow' : 'gray'}
        >
          {cellText(value)}
        </Badge>
      )
    }
    case 'ce_decision':
    case 'final_decision': {
      if (colValue === 'final_decision') {
        const live = String(record.ce_decision ?? '')
        const finalDec = String(value ?? '')
        if (!finalDec || finalDec === 'None') {
          return <Text size="xs" c="dimmed">-</Text>
        }
        const changed = live && finalDec && live !== finalDec
        return (
          <Badge
            size="sm"
            variant={changed ? 'filled' : 'light'}
            color={value === 'REPORTABLE' || value === 'Reportable' ? 'red' : value === 'NOT_REPORTABLE' || value === 'Not Reportable' ? 'green' : value === 'REQUIRES_REVIEW' || value === 'MANUAL_REVIEW' || value === 'Manual Review' ? 'orange' : 'gray'}
          >
            {cellText(value)}{changed ? ' · changed' : ''}
          </Badge>
        )
      }
      return (
        <Badge
          size="sm"
          color={value === 'REPORTABLE' ? 'red' : value === 'NOT_REPORTABLE' ? 'green' : value === 'REQUIRES_REVIEW' ? 'orange' : 'gray'}
        >
          {cellText(value)}
        </Badge>
      )
    }
    case 'charge':
      return (
        <Tooltip label={cellText(value)} withArrow multiline maw={400}>
          <Text size="xs" lineClamp={2} fw={500}>{cellText(value)}</Text>
        </Tooltip>
      )
    case 'charge_norm':
    case 'disposition':
    case 'rationale':
    case 'rules_applied':
      return (
        <Tooltip label={cellText(value)} withArrow multiline maw={400}>
          <Text size="xs" lineClamp={2}>{cellText(value)}</Text>
        </Tooltip>
      )
    case 'disposition_date': {
      if (value == null || value === '') return <Text size="xs">None</Text>
      const raw = typeof value === 'object' && value && 'value' in value
        ? (value as { value: unknown }).value
        : value
      const date = new Date(String(raw))
      return <Text size="xs">{Number.isNaN(date.getTime()) ? String(raw) : date.toLocaleDateString()}</Text>
    }
    default:
      return <Text size="xs">{cellText(value)}</Text>
  }
}

export function MeteringDashboard() {
  const { isExpert } = usePermissions()
  const [preset, setPreset] = useState('30d')
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getPresetRange('30d'))
  const [search, setSearch] = useState('')
  const [requestSource, setRequestSource] = useState('client')
  const [page, setPage] = useState(1)
  const [selectedOffense, setSelectedOffense] = useState<any>(null)
  const [dialogOpened, setDialogOpened] = useState(false)
  const [routingExpanded, setRoutingExpanded] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS)
  const [routingSearch, setRoutingSearch] = useState('')
  const [sortCol, setSortCol] = useState<string>('total_api_calls')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const pageSize = 50

  const toggleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  const handlePresetChange = (value: string) => {
    setPreset(value)
    if (value !== 'custom') {
      setDateRange(getPresetRange(value))
    }
  }

  const filter = useMemo(() => ({
    startDate: toLocalYmd(dateRange[0]),
    endDate: toLocalYmd(dateRange[1]),
    requestSource,
  }), [dateRange, requestSource])

  const telemetryFilter = useMemo(() => ({
    startDate: filter.startDate,
    endDate: filter.endDate,
    requestSource,
  }), [filter.startDate, filter.endDate, requestSource])

  const {
    data: metering,
    isLoading: meteringLoading,
    isError: meteringError,
    refetch: refetchMetering,
  } = useMeteringDashboard(filter)
  const {
    data: telemetry,
    isLoading: telemetryLoading,
    isError: telemetryError,
    refetch: refetchTelemetry,
  } = useTelemetryDashboard(telemetryFilter)

  const {
    data: routingRowsData,
    isLoading: routingLoading,
    isFetching: routingFetching,
    refetch: refetchRouting,
  } = useRoutingRows({
    startDate: filter.startDate,
    endDate: filter.endDate,
    searchId: routingSearch.trim() || undefined,
    page,
    pageSize,
    includeMetrics: false,
    liveApiTraffic: true,
    requestSource,
    enabled: routingExpanded,
  })

  const routingRows = useMemo(() => {
    const rows = routingRowsData?.rows || []
    const q = routingSearch.trim()
    if (!q) return rows
    return rows.filter((r: any) =>
      String(r.search_id ?? '').includes(q) ||
      String(r.offense_id ?? '').includes(q),
    )
  }, [routingRowsData, routingSearch])
  const routingTotal = routingRowsData?.totalCount || 0
  const routingPages = Math.max(1, Math.ceil(routingTotal / pageSize))

  const { data: dialogSearchRows } = useRoutingRows({
    startDate: filter.startDate,
    endDate: filter.endDate,
    searchId: selectedOffense?.search_id,
    page: 1,
    pageSize: 50,
    includeMetrics: false,
    liveApiTraffic: true,
    requestSource,
    enabled: dialogOpened && !!selectedOffense?.search_id,
  })

  const offensesInSearch = useMemo(() => {
    const rows = dialogSearchRows?.rows || []
    if (rows.length > 0) return rows
    return selectedOffense ? [selectedOffense] : []
  }, [dialogSearchRows, selectedOffense])

  useEffect(() => {
    setPage(1)
  }, [filter.startDate, filter.endDate, routingSearch, requestSource])

  // API calls / latency / offense volume: bronze telemetry (HTTP requests).
  // Queue counts: only searches that had those same live API calls.
  type DisplayRow = ClientMeteringRow & {
    avg_latency_ms: number | null
    p95_latency_ms: number | null
    has_decisions: boolean
    decided_offenses: number
  }

  const displayRows = useMemo((): DisplayRow[] => {
    const meteringByTenant = new Map(
      (metering?.rows ?? []).map((r) => [r.tenant_id, r]),
    )

    return (telemetry?.rows ?? [])
      .map((t) => {
        const m = meteringByTenant.get(t.tenant_id)
        const telemetryName =
          t.client_name && t.client_name !== t.tenant_id ? t.client_name : null
        const hasDecisions = !!m && (
          m.total_offenses > 0
          || m.automation_count > 0
          || m.auditor_count > 0
          || m.insufficient_data_count > 0
        )
        return {
          tenant_id: t.tenant_id,
          tenant_name: m?.tenant_name || telemetryName,
          total_api_calls: t.total_requests,
          successful_calls: t.successful_requests,
          failed_calls: t.failed_requests,
          total_offenses: hasDecisions && m ? m.total_offenses : 0,
          automation_count: hasDecisions && m ? m.automation_count : 0,
          auditor_count: hasDecisions && m ? m.auditor_count : 0,
          insufficient_data_count: hasDecisions && m ? m.insufficient_data_count : 0,
          missing_info_count: hasDecisions && m ? (m.missing_info_count ?? 0) : 0,
          low_confidence_count: hasDecisions && m ? (m.low_confidence_count ?? 0) : 0,
          mr_insufficient_data_count: hasDecisions && m ? (m.mr_insufficient_data_count ?? 0) : 0,
          mr_model_enhancements_count: hasDecisions && m ? (m.mr_model_enhancements_count ?? 0) : 0,
          avg_latency_ms: t.avg_latency_ms,
          p95_latency_ms: t.p95_latency_ms,
          has_decisions: hasDecisions,
          decided_offenses: hasDecisions && m ? m.total_offenses : 0,
        }
      })
      .sort((a, b) => b.total_api_calls - a.total_api_calls)
  }, [telemetry, metering])

  const metrics = useMemo(() => {
    if (!telemetry?.metrics) return null
    const hasDecisions = displayRows.some((r) => r.has_decisions)
    return {
      totalApiCalls: telemetry.metrics.totalRequests,
      totalSuccessful: telemetry.metrics.totalSuccessful,
      totalFailed: telemetry.metrics.totalFailed,
      totalOffenses: displayRows.reduce((s, r) => s + r.total_offenses, 0),
      totalAutomation: displayRows.reduce((s, r) => s + r.automation_count, 0),
      totalAuditor: displayRows.reduce((s, r) => s + r.auditor_count, 0),
      totalInsufficientData: displayRows.reduce(
        (s, r) => s + r.insufficient_data_count,
        0,
      ),
      totalMissingInfo: displayRows.reduce((s, r) => s + r.missing_info_count, 0),
      totalLowConfidence: displayRows.reduce((s, r) => s + r.low_confidence_count, 0),
      totalMrInsufficientData: displayRows.reduce((s, r) => s + r.mr_insufficient_data_count, 0),
      totalMrModelEnhancements: displayRows.reduce((s, r) => s + r.mr_model_enhancements_count, 0),
      decidedOffenses: displayRows.reduce((s, r) => s + r.decided_offenses, 0),
      hasDecisions,
      avgLatencyMs: telemetry.metrics.avgLatencyMs,
      p50LatencyMs: telemetry.metrics.p50LatencyMs,
      p95LatencyMs: telemetry.metrics.p95LatencyMs,
      p99LatencyMs: telemetry.metrics.p99LatencyMs,
    }
  }, [telemetry, displayRows])

  const trafficChart = useMemo(() => {
    const hourly = telemetry?.hourly ?? []
    const start = filter.startDate
    const end = filter.endDate
    if (!start || !end) return { data: [], grain: 'hourly' as const, quiet: 0 }

    if (hourly.length > 0) {
      const singleDay = start === end
      const data = hourly.map((p) => ({
        label: formatHourLabel(p.hour, singleDay),
        requests: p.total_requests,
      }))
      return {
        data,
        grain: 'hourly' as const,
        quiet: data.filter((d) => d.requests === 0).length,
      }
    }

    const byDate = new Map((telemetry?.daily ?? []).map((p) => [p.date, p.total_requests]))
    const data: { label: string; requests: number }[] = []
    for (let cur = start; cur <= end; cur = addDaysYmd(cur, 1)) {
      data.push({ label: formatDayLabel(cur), requests: byDate.get(cur) ?? 0 })
    }
    return {
      data,
      grain: 'daily' as const,
      quiet: data.filter((d) => d.requests === 0).length,
    }
  }, [telemetry, filter.startDate, filter.endDate])

  const searchQueueDist = useMemo(() => {
    const searches = metering?.searches ?? []
    const counts = {
      AUTOMATION: 0,
      AUDITOR: 0,
      INSUFFICIENT_DATA: 0,
      MR_INSUFFICIENT_DATA: 0,
      MR_MODEL_ENHANCEMENTS: 0,
      MR_IDENTITY: 0,
    }
    for (const s of searches) {
      const q = String(s.search_queue || '').toUpperCase().replace(/\s+/g, '_')
      if (q === 'AUTOMATION') {
        counts.AUTOMATION += 1
      } else if (q === 'AUDITOR') {
        counts.AUDITOR += 1
      } else if (q === 'INSUFFICIENT_DATA' || q === 'MISSING_INFO' || q === 'LOW_CONFIDENCE') {
        counts.INSUFFICIENT_DATA += 1
        const cat = String(s.mr_category || '').toUpperCase()
        if (cat === 'IDENTITY') counts.MR_IDENTITY += 1
        else if (cat === 'INSUFFICIENT_DATA') counts.MR_INSUFFICIENT_DATA += 1
        else counts.MR_MODEL_ENHANCEMENTS += 1
      }
    }
    return { searches, counts, total: searches.length }
  }, [metering])

  const filteredRows = useMemo(() => {
    let rows = displayRows
    if (search.trim()) {
      const q = search.toLowerCase()
      rows = rows.filter(
        (r) =>
          r.tenant_id.toLowerCase().includes(q) ||
          (r.tenant_name?.toLowerCase().includes(q)),
      )
    }
    return [...rows].sort((a, b) => {
      const aVal = (a as any)[sortCol] ?? 0
      const bVal = (b as any)[sortCol] ?? 0
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    })
  }, [displayRows, search, sortCol, sortDir])

  useEffect(() => {
    if (telemetry || metering) setLastUpdated(new Date())
  }, [telemetry, metering])

  const successRate = metrics
    ? pct(metrics.totalSuccessful, metrics.totalApiCalls)
    : '0.0'

  const volume = metering?.volume
  const uniqueSearches = volume?.uniqueSearches || searchQueueDist.total || metrics?.totalApiCalls || 0
  const aiDecidedSearches = searchQueueDist.counts.AUTOMATION + searchQueueDist.counts.AUDITOR
  const mrSearches = searchQueueDist.counts.INSUFFICIENT_DATA
  const aiDecisionRate = pct(aiDecidedSearches, uniqueSearches)
  const searchTypes = metering?.searchTypes ?? []
  const rootCauses = metering?.rootCauses ?? []

  const isLoading = telemetryLoading || meteringLoading
  const isError = telemetryError

  const handleRefresh = () => {
    void refetchTelemetry()
    void refetchMetering()
    void refetchRouting()
  }

  const exportRoutingCsv = useCallback(() => {
    if (!routingRows.length) return
    const cols = DETAIL_COLUMNS.filter((c) => visibleColumns.includes(c.value))
    const header = cols.map((c) => c.label).join(',')
    const rows = routingRows.map((r: any) =>
      cols.map((c) => {
        const text = cellText(r[c.value]).replace(/"/g, '""')
        return `"${text}"`
      }).join(','),
    )
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `routing-decisions-${filter.startDate}-${filter.endDate}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [routingRows, visibleColumns, filter.startDate, filter.endDate])

  return (
    <Stack gap="lg">
      <PageHeader
        title="API Metering"
        subtitle="API calls, then the offenses and queues from those same calls"
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />

      <Group align="flex-end" gap="md">
        <FilterBar
          preset={preset}
          onPresetChange={handlePresetChange}
          dateRange={dateRange}
          onDateRangeChange={(value) => {
            setPreset('custom')
            setDateRange(value)
          }}
          presets={METERING_PRESETS}
        />
        <SegmentedControl
          value={requestSource}
          onChange={setRequestSource}
          data={[
            { label: 'Client', value: 'client' },
            { label: 'Internal', value: 'internal' },
            { label: 'All', value: 'all' },
          ]}
          size="xs"
        />
      </Group>

      {isError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error loading metering data">
          Failed to fetch API telemetry. Please try again.
        </Alert>
      )}

      {meteringError && !isError && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="Offense stats unavailable">
          API call counts loaded, but decision/queue stats failed to load.
        </Alert>
      )}

      {metrics && metrics.totalApiCalls > 0 && !metrics.hasDecisions && !meteringError && (
        <Alert icon={<IconHelpCircle size={16} />} color="gray" title="No routing for these API calls">
          Queue stats are only for the API calls above. If this is empty, routing gold has not landed yet.
        </Alert>
      )}

      {telemetryLoading && !telemetry && (
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Loading metering data...</Text>
        </Group>
      )}

      {metrics && (
        <>
          {/* Daily report KPIs — same definitions as the UBS Daily Search Report */}
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <MetricCard
                label="Unique Searches"
                value={fmt(uniqueSearches)}
                subtitle={
                  metrics.totalApiCalls !== uniqueSearches
                    ? `${fmt(metrics.totalApiCalls)} API calls`
                    : 'Received in this window (CT)'
                }
                icon={<IconSearch size={20} />}
                color="blue"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <MetricCard
                label="AI Decision Rate"
                value={`${aiDecisionRate}%`}
                subtitle={`${fmt(aiDecidedSearches)} Reportable or Not Reportable, no manual review`}
                icon={<IconRobot size={20} />}
                color="teal"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <MetricCard
                label="Sent to Manual Review"
                value={fmt(mrSearches)}
                subtitle={`${pct(mrSearches, uniqueSearches)}% of unique searches`}
                icon={<IconHelpCircle size={20} />}
                color="yellow"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <MetricCard
                label="In-Scope Offenses"
                value={fmt(volume?.offensesInScope || metrics.totalOffenses)}
                subtitle={
                  volume
                    ? `${fmt(volume.offensesSent)} sent · ${fmt(volume.offensesExcluded)} excluded by client`
                    : undefined
                }
                icon={<IconGavel size={20} />}
                color="orange"
              />
            </Grid.Col>
          </Grid>

          <Card>
            <Text fw={600} size="sm">Volume: Sent vs. In Scope</Text>
            <Text size="xs" c="dimmed" mb="sm">
              Offenses flagged excluded by the client receive no adjudication. A search or case is In Scope if it has at least one non-excluded offense.
            </Text>
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Level</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Sent</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Excluded by Client</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>In Scope</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {([
                  ['Searches', volume?.searchesSent ?? uniqueSearches, volume?.searchesExcluded ?? 0, volume?.searchesInScope ?? uniqueSearches],
                  ['Cases', volume?.casesSent ?? 0, volume?.casesExcluded ?? 0, volume?.casesInScope ?? 0],
                  ['Offenses', volume?.offensesSent ?? metrics.totalOffenses, volume?.offensesExcluded ?? 0, volume?.offensesInScope ?? metrics.totalOffenses],
                ] as const).map(([level, sent, excluded, inScope]) => (
                  <Table.Tr key={level}>
                    <Table.Td>{level}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmt(sent)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmt(excluded)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmt(inScope)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <MetricCard
                label="Not Reportable"
                value={searchQueueDist.total > 0 ? fmt(searchQueueDist.counts.AUTOMATION) : '-'}
                subtitle={
                  searchQueueDist.total > 0
                    ? `${pct(searchQueueDist.counts.AUTOMATION, uniqueSearches)}% of unique searches`
                    : 'No search decisions yet'
                }
                icon={<IconCheck size={20} />}
                color="teal"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <MetricCard
                label="Reportable"
                value={searchQueueDist.total > 0 ? fmt(searchQueueDist.counts.AUDITOR) : '-'}
                subtitle={
                  searchQueueDist.total > 0
                    ? `${pct(searchQueueDist.counts.AUDITOR, uniqueSearches)}% of unique searches`
                    : 'No search decisions yet'
                }
                icon={<IconUserCheck size={20} />}
                color="red"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <MetricCard
                label="Manual Review"
                value={searchQueueDist.total > 0 ? fmt(mrSearches) : '-'}
                subtitle={
                  searchQueueDist.total > 0
                    ? `${pct(mrSearches, uniqueSearches)}% of unique searches`
                    : 'No search decisions yet'
                }
                icon={<IconHelpCircle size={20} />}
                color="yellow"
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <MetricCard
                label="Automation (offenses)"
                value={metrics.hasDecisions ? fmt(metrics.totalAutomation) : '-'}
                subtitle={
                  metrics.hasDecisions
                    ? `${pct(metrics.totalAutomation, metrics.decidedOffenses)}% of in-scope offenses`
                    : 'No routing for these API calls'
                }
                icon={<IconRobot size={20} />}
                color="teal"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <MetricCard
                label="Auditor (offenses)"
                value={metrics.hasDecisions ? fmt(metrics.totalAuditor) : '-'}
                subtitle={
                  metrics.hasDecisions
                    ? `${pct(metrics.totalAuditor, metrics.decidedOffenses)}% of in-scope offenses`
                    : 'No routing for these API calls'
                }
                icon={<IconUserCheck size={20} />}
                color="blue"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <MetricCard
                label="Insufficient Data (offenses)"
                value={metrics.hasDecisions ? fmt(metrics.totalInsufficientData) : '-'}
                subtitle={
                  metrics.hasDecisions
                    ? `${pct(metrics.totalInsufficientData, metrics.decidedOffenses)}% of in-scope offenses`
                    : 'No routing for these API calls'
                }
                icon={<IconHelpCircle size={20} />}
                color="yellow"
              />
            </Grid.Col>
          </Grid>

          {trafficChart.data.length > 0 && (
            <ChartCard
              title="Traffic through the day"
              subtitle={
                trafficChart.grain === 'hourly'
                  ? `Hourly API calls (CT). ${trafficChart.quiet} quiet hour${trafficChart.quiet === 1 ? '' : 's'} with no requests.`
                  : `Daily API calls. ${trafficChart.quiet} day${trafficChart.quiet === 1 ? '' : 's'} with no requests. Use Today or 7d for hourly.`
              }
            >
              <BarChart
                h={240}
                data={trafficChart.data}
                dataKey="label"
                series={[{ name: 'requests', color: 'blue.6', label: 'API calls' }]}
                tickLine="y"
                withLegend={false}
                xAxisProps={
                  trafficChart.data.length > 24
                    ? { interval: Math.ceil(trafficChart.data.length / 12) - 1, angle: -40, textAnchor: 'end', height: 56 }
                    : undefined
                }
              />
            </ChartCard>
          )}

          <Grid>
            <Grid.Col span={12}>
              <Card>
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <IconClock size={18} color="var(--mantine-color-violet-6)" />
                    <Text fw={600} size="sm">Response Latency</Text>
                  </Group>
                  {lastUpdated && (
                    <Text size="xs" c="dimmed">
                      Last updated: {lastUpdated.toLocaleTimeString()}
                    </Text>
                  )}
                </Group>
                <Group grow mt="sm">
                  <Stack gap={2} align="center">
                    <Text size="xs" c="dimmed">Avg</Text>
                    <Text fw={700}>{fmtMs(metrics.avgLatencyMs)}</Text>
                  </Stack>
                  <Stack gap={2} align="center">
                    <Text size="xs" c="dimmed">p50</Text>
                    <Text fw={700}>{fmtMs(metrics.p50LatencyMs)}</Text>
                  </Stack>
                  <Stack gap={2} align="center">
                    <Text size="xs" c="dimmed">p95</Text>
                    <Text fw={700} c="orange">{fmtMs(metrics.p95LatencyMs)}</Text>
                  </Stack>
                  <Stack gap={2} align="center">
                    <Text size="xs" c="dimmed">p99</Text>
                    <Text fw={700} c="red">{fmtMs(metrics.p99LatencyMs)}</Text>
                  </Stack>
                </Group>
              </Card>
            </Grid.Col>
          </Grid>

          {/* Visual breakdowns */}
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <ChartCard
                title="AI Decisions"
                subtitle={
                  searchQueueDist.total > 0
                    ? `Search-level rollup (${fmt(searchQueueDist.total)} searches). Same as Reportable / Not Reportable / Manual Review on the daily report.`
                    : 'No routing searches for these API calls yet'
                }
              >
                <QueueDistributionBars
                  automation={searchQueueDist.counts.AUTOMATION}
                  auditor={searchQueueDist.counts.AUDITOR}
                  insufficientData={searchQueueDist.counts.INSUFFICIENT_DATA}
                  nested={[
                    { key: 'INSUFFICIENT_DATA', count: searchQueueDist.counts.MR_INSUFFICIENT_DATA, color: 'yellow.6' },
                    { key: 'MODEL_ENHANCEMENTS', count: searchQueueDist.counts.MR_MODEL_ENHANCEMENTS, color: 'orange.6' },
                    { key: 'IDENTITY', count: searchQueueDist.counts.MR_IDENTITY, color: 'grape.6' },
                  ]}
                  total={searchQueueDist.total}
                  hasDecisions={searchQueueDist.total > 0}
                  automationLabel="Not Reportable"
                  auditorLabel="Reportable"
                  insuffLabel="Manual Review"
                />
              </ChartCard>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <ChartCard
                title="Offense Queue Distribution"
                subtitle={
                  metrics.hasDecisions
                    ? 'Routing queues for in-scope offenses. Manual-review offenses split into Insufficient Data vs Model Enhancements.'
                    : 'No routing decisions for these API calls yet'
                }
              >
                <QueueDistributionBars
                  automation={metrics.totalAutomation}
                  auditor={metrics.totalAuditor}
                  insufficientData={metrics.totalInsufficientData}
                  nested={[
                    { key: 'INSUFFICIENT_DATA', count: metrics.totalMrInsufficientData, color: 'yellow.6' },
                    { key: 'MODEL_ENHANCEMENTS', count: metrics.totalMrModelEnhancements, color: 'orange.6' },
                  ]}
                  total={metrics.decidedOffenses}
                  hasDecisions={metrics.hasDecisions}
                />
              </ChartCard>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <ChartCard title="API Call Status" subtitle="Success vs failure ratio">
                <Group justify="center" py="md">
                  <RingProgress
                    size={180}
                    thickness={20}
                    roundCaps
                    sections={[
                      { value: Number(successRate), color: 'teal' },
                      { value: Number(pct(metrics.totalFailed, metrics.totalApiCalls)), color: 'red' },
                    ]}
                    label={
                      <Stack gap={0} align="center">
                        <Text fw={700} size="xl">{successRate}%</Text>
                        <Text size="xs" c="dimmed">Success</Text>
                      </Stack>
                    }
                  />
                  <Stack gap="xs">
                    <Group gap="xs">
                      <Badge color="teal" variant="dot" size="lg">
                        Successful: {fmt(metrics.totalSuccessful)}
                      </Badge>
                    </Group>
                    <Group gap="xs">
                      <Badge color="red" variant="dot" size="lg">
                        Failed: {fmt(metrics.totalFailed)}
                      </Badge>
                    </Group>
                  </Stack>
                </Group>
              </ChartCard>
            </Grid.Col>

            {searchTypes.length > 0 && (
              <Grid.Col span={{ base: 12, md: 6 }}>
                <ChartCard title="Volume by Search Type" subtitle="Unique searches from the live request payload">
                  <Table withTableBorder withColumnBorders mt="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Search Type</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Unique Searches</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {searchTypes.map((row) => (
                        <Table.Tr key={row.search_type}>
                          <Table.Td>{row.search_type}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{fmt(row.unique_searches)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ChartCard>
              </Grid.Col>
            )}
          </Grid>

          {rootCauses.length > 0 && (
            <Card>
              <Text fw={600} size="sm">Details of Missing Data at Offense Level</Text>
              <Text size="xs" c="dimmed" mb="sm">
                In-scope offenses only. An offense can appear in more than one root cause.
              </Text>
              <Table withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Root Cause</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Offenses Affected</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rootCauses.map((row) => (
                    <Table.Tr key={row.root_cause}>
                      <Table.Td>{row.root_cause}</Table.Td>
                      <Table.Td>{row.category}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{fmt(row.offense_count)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          )}

          {/* Client breakdown table */}
          <Card>
            <Group justify="space-between" mb="md">
              <div>
                <Text fw={600}>Client Usage Breakdown</Text>
                <Text size="xs" c="dimmed">
                  Same API calls as the cards above; queues are those offenses only
                </Text>
              </div>
              <TextInput
                placeholder="Search by client name or ID..."
                leftSection={<IconSearch size={14} />}
                size="xs"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ minWidth: 250 }}
              />
            </Group>

            <ScrollArea>
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Client</Table.Th>
                    {[
                      { key: 'total_api_calls', label: 'API Calls' },
                      { key: 'successful_calls', label: 'Success/Fail' },
                      { key: 'total_offenses', label: 'Offenses' },
                      { key: 'automation_count', label: 'Automation' },
                      { key: 'auditor_count', label: 'Auditor' },
                      { key: 'insufficient_data_count', label: 'Insuff. Data' },
                      { key: 'avg_latency_ms', label: 'Avg Latency' },
                      { key: 'p95_latency_ms', label: 'p95 Latency' },
                    ].map(({ key, label }) => (
                      <Table.Th
                        key={key}
                        style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => toggleSort(key)}
                      >
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <Text size="xs" fw={600}>{label}</Text>
                          {sortCol === key
                            ? (sortDir === 'asc' ? <IconArrowUp size={12} /> : <IconArrowDown size={12} />)
                            : <IconArrowsSort size={12} color="gray" />}
                        </Group>
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredRows.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={9}>
                        <Text ta="center" c="dimmed" py="lg">
                          {search ? 'No clients match your search' : 'No metering data available'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    filteredRows.map((row) => (
                      <Table.Tr key={row.tenant_id}>
                        <Table.Td>
                          <Stack gap={2}>
                            <Text size="sm" fw={500}>
                              {row.tenant_name || row.tenant_id}
                            </Text>
                            {row.tenant_name && (
                              <Text size="xs" c="dimmed">{row.tenant_id}</Text>
                            )}
                          </Stack>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" fw={600}>{fmt(row.total_api_calls)}</Text>
                        </Table.Td>
                        <Table.Td style={{ minWidth: 140 }}>
                          <Group gap={4} wrap="nowrap">
                            <Text size="xs" c="teal" fw={500}>{fmt(row.successful_calls)}</Text>
                            <Progress.Root size="sm" style={{ flex: 1 }}>
                              <Progress.Section value={Number(pct(row.successful_calls, row.total_api_calls))} color="teal" />
                              <Progress.Section value={Number(pct(row.failed_calls, row.total_api_calls))} color="red" />
                            </Progress.Root>
                            <Text size="xs" c="red" fw={500}>{row.failed_calls}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm">{fmt(row.total_offenses)}</Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" c={row.has_decisions ? 'teal' : 'dimmed'}>
                            {fmtQueue(row.automation_count, row.has_decisions)}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" c={row.has_decisions ? 'blue' : 'dimmed'}>
                            {fmtQueue(row.auditor_count, row.has_decisions)}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Stack gap={2} align="flex-end">
                            <Text size="sm" c={row.has_decisions ? 'yellow.7' : 'dimmed'}>
                              {fmtQueue(row.insufficient_data_count, row.has_decisions)}
                            </Text>
                            {row.has_decisions && row.insufficient_data_count > 0 && (
                              <Text size="xs" c="dimmed">
                                {row.missing_info_count} MR-miss / {row.low_confidence_count} MR-low
                              </Text>
                            )}
                          </Stack>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" c="dimmed">{fmtMs(row.avg_latency_ms)}</Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm" c="dimmed">{fmtMs(row.p95_latency_ms)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>

          <Card>
            <UnstyledButton
              onClick={() => setRoutingExpanded((v) => !v)}
              style={{ width: '100%' }}
            >
              <Group justify="space-between">
                <Group gap="xs">
                  {routingExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                  <div>
                    <Text fw={600}>Routing decisions</Text>
                    <Text size="xs" c="dimmed">
                      Offense queue from these live API calls (Automation / Auditor / Insufficient Data). MR reason splits Insufficient Data only.
                      {routingTotal > 0 && ` (${routingTotal.toLocaleString()} offenses)`}
                    </Text>
                  </div>
                </Group>
                {routingFetching && !routingLoading && (
                  <Group gap="xs">
                    <Loader size="xs" />
                    <Text size="xs" c="dimmed">Updating...</Text>
                  </Group>
                )}
                {routingTotal > 0 && (
                  <Tooltip label="Export visible columns to CSV">
                    <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); exportRoutingCsv() }}>
                      <IconDownload size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </UnstyledButton>

            <Collapse in={routingExpanded}>
              <Stack gap="md" mt="md">
                <Group gap="md" align="flex-end">
                  <TextInput
                    placeholder="Search by search ID or offense ID..."
                    leftSection={<IconSearch size={14} />}
                    size="xs"
                    value={routingSearch}
                    onChange={(e) => { setRoutingSearch(e.currentTarget.value); setPage(1) }}
                    style={{ minWidth: 280 }}
                    label="Search / Offense ID"
                  />
                  <MultiSelect
                    data={DETAIL_COLUMNS.map((c) => ({ value: c.value, label: c.label }))}
                    value={visibleColumns}
                    onChange={setVisibleColumns}
                    placeholder="Select columns to display"
                    searchable
                    clearable
                    size="xs"
                    label="Visible columns"
                    maxDropdownHeight={300}
                    style={{ flex: 1 }}
                  />
                </Group>
                {routingLoading ? (
                  <Group justify="center" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Loading routing decisions...</Text>
                  </Group>
                ) : routingTotal === 0 ? (
                  <Text ta="center" c="dimmed" py="lg">
                    No routing decisions in this range
                  </Text>
                ) : (
                  <>
                    <ScrollArea>
                      <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                          <Table.Tr>
                            {DETAIL_COLUMNS.filter((col) => visibleColumns.includes(col.value)).map((col) => (
                              <Table.Th key={col.value} style={{ whiteSpace: 'nowrap' }}>{col.label}</Table.Th>
                            ))}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {routingRows.map((record: any, index: number) => (
                            <Table.Tr
                              key={`${record.offense_id}-${index}`}
                              onClick={() => {
                                setSelectedOffense(record)
                                setDialogOpened(true)
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {DETAIL_COLUMNS.filter((col) => visibleColumns.includes(col.value)).map((col) => (
                                <Table.Td
                                  key={col.value}
                                  style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {renderDetailCell(record, col.value)}
                                </Table.Td>
                              ))}
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                    {routingPages > 1 && (
                      <Group justify="flex-end">
                        <Pagination total={routingPages} value={page} onChange={setPage} size="sm" />
                      </Group>
                    )}
                  </>
                )}
              </Stack>
            </Collapse>
          </Card>
        </>
      )}

      <OffenseDetailDialog
        opened={dialogOpened}
        onClose={() => {
          setDialogOpened(false)
          setSelectedOffense(null)
        }}
        offense={selectedOffense}
        allOffensesInSearch={offensesInSearch}
        isExpert={isExpert}
      />
    </Stack>
  )
}
